import AccountSync from "../services/AccountSync.js";
import AccountService from "../services/AccountService.js";
import {
    BROWS_OPTIONS, EYES_OPTIONS, GLASSES_OPTIONS, MOUTH_OPTIONS, NOSE_OPTIONS, AVATAR_COLORS,
    getDefaultAvatar, randomAvatar, sanitizeAvatar, renderAvatarSVG, AVATAR_STORAGE_KEY,
} from "../utils/AvatarBuilder.js";

const CONFIRM_FLASH_MS = 1200;

const ARROW_UP = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 15l-6-6-6 6"/></svg>`;
const ARROW_DOWN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`;

// Pictogrammes représentant chaque catégorie de traits (pas d'aperçu miniature :
// juste un repère visuel simple, façon icônes du header).
const ICONS = {
    brows: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 10c2-3 6-3 8-1"/><path d="M13 9c2-2 6-2 8 1"/></svg>`,
    eyes: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/><circle cx="12" cy="12" r="3"/></svg>`,
    glasses: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6.5" cy="13" r="4"/><circle cx="17.5" cy="13" r="4"/><path d="M10.5 13h3"/><path d="M2.5 13 1 10"/><path d="M21.5 13 23 10"/></svg>`,
    mouth: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 9c2 6 14 6 16 0"/></svg>`,
    nose: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4c1 3 2 7 2 10a3 3 0 0 1-3 3"/></svg>`,
};

const CATEGORIES = [
    { key: "brows", options: BROWS_OPTIONS },
    { key: "eyes", options: EYES_OPTIONS },
    { key: "glasses", options: GLASSES_OPTIONS },
    { key: "mouth", options: MOUTH_OPTIONS },
    { key: "nose", options: NOSE_OPTIONS },
];

/**
 * Page dédiée à la création/modification de l'avatar (voir AvatarBuilder.js
 * pour le rendu SVG — style DiceBear "Notionists Neutral"). Les flèches ne
 * modifient qu'un brouillon en mémoire : rien n'est enregistré (localStorage
 * ni serveur) tant que "Valider" n'est pas cliqué.
 */
export default class AvatarPage {
    constructor() {
        this.page = document.querySelector(".page.avatar");
        if (!this.page) return;

        this.preview = this.page.querySelector(".avatar-preview");
        this.stepperRow = this.page.querySelector(".avatar-stepper-row");
        this.confirmBtn = this.page.querySelector(".avatar-confirm");
        this.syncNote = this.page.querySelector(".avatar-sync-note");
        this.status = this.page.querySelector(".avatar-status");
        this.flashTimer = null;
        this.saveRequestId = 0;
        this.savePromise = Promise.resolve();
        // Le joueur a-t-il déjà exprimé un choix (flèches, aléatoire, "Valider") ?
        // Une fois vrai, `init()` ne réadopte plus la valeur du localStorage.
        this.userTouched = false;
        this.t = (k) => window.geoNerdApp.languageManager.t(k);

        this.config = this.loadLocal();
        this.savedSnapshot = JSON.stringify(this.config);

        this.renderSteppers();
        this.bindActions();
        this.renderAll();

        this.init();
    }

    /** Attend la session avant un second rendu : la reconciliation serveur↔local
     * (AccountSync.syncAvatar) peut avoir mis à jour localStorage entre-temps. */
    async init() {
        if (window.geoNerdApp?.accountReady) await window.geoNerdApp.accountReady;
        // Cette attente enchaîne plusieurs appels réseau (/auth/me → pull → push
        // → syncAvatar) : l'éditeur est utilisable pendant ce temps. Réadopter la
        // valeur du localStorage sans condition écrasait silencieusement le
        // brouillon en cours — le choix du joueur prime donc dès qu'il a touché à
        // quelque chose.
        const stored = this.loadLocal();
        if (!this.userTouched) this.config = stored;
        // La référence "enregistrée" suit toujours le localStorage réconcilié,
        // sinon "Valider" resterait grisé alors que le brouillon diffère de ce qui
        // est réellement stocké (avatar d'un autre appareil, par exemple).
        this.savedSnapshot = JSON.stringify(stored);
        this.renderAll();
        this.refreshSyncNote();
    }

    loadLocal() {
        try {
            return sanitizeAvatar(JSON.parse(localStorage.getItem(AVATAR_STORAGE_KEY)));
        } catch {
            return getDefaultAvatar();
        }
    }

    refreshSyncNote() {
        this.syncNote?.classList.toggle("is-hidden", AccountSync.isLoggedIn());
    }

    renderSteppers() {
        const stepper = (category, iconHtml) => {
            const label = this.t(`avatar.${category}`);
            return `
            <div class="avatar-stepper" data-category="${category}">
                <button type="button" class="avatar-stepper-arrow" data-dir="1" aria-label="${label} +">${ARROW_UP}</button>
                <div class="avatar-stepper-icon" aria-label="${label}" role="img">${iconHtml}</div>
                <button type="button" class="avatar-stepper-arrow" data-dir="-1" aria-label="${label} -">${ARROW_DOWN}</button>
            </div>
        `;
        };

        this.stepperRow.innerHTML = CATEGORIES.map((c) => stepper(c.key, ICONS[c.key])).join("")
            + stepper("color", `<span class="avatar-color-swatch"></span>`);

        this.stepperRow.querySelectorAll(".avatar-stepper").forEach((el) => {
            const category = el.dataset.category;
            el.querySelectorAll(".avatar-stepper-arrow").forEach((btn) =>
                btn.addEventListener("click", () => this.step(category, Number(btn.dataset.dir)))
            );
        });
    }

    step(category, dir) {
        if (category === "color") {
            const i = AVATAR_COLORS.indexOf(this.config.color);
            const next = AVATAR_COLORS[(i + dir + AVATAR_COLORS.length) % AVATAR_COLORS.length];
            this.setDraft({ color: next });
            return;
        }
        const options = CATEGORIES.find((c) => c.key === category).options;
        const i = options.indexOf(this.config[category]);
        const next = options[(i + dir + options.length) % options.length];
        this.setDraft({ [category]: next });
    }

    bindActions() {
        this.page.querySelector(".avatar-random").addEventListener("click", () => this.setDraft(randomAvatar()));
        this.page.querySelector(".avatar-reset").addEventListener("click", () => this.setDraft(getDefaultAvatar()));
        this.confirmBtn.addEventListener("click", () => this.confirm());
        this.syncNote?.addEventListener("click", () => document.querySelector(".btn-account")?.click());
    }

    /** Change uniquement le brouillon affiché — pas d'écriture disque tant que "Valider" n'est pas cliqué. */
    setDraft(patch) {
        this.userTouched = true;
        this.config = sanitizeAvatar({ ...this.config, ...patch });
        this.clearStatus(); // le message de confirmation ne vaut plus pour ce nouveau brouillon
        this.renderAll();
    }

    async confirm() {
        // Un choix validé pendant l'attente de la session ne doit pas non plus
        // être repris par `init()` : syncAvatar peut réécrire le localStorage
        // juste après (avatar déjà présent côté serveur).
        this.userTouched = true;
        localStorage.setItem(AVATAR_STORAGE_KEY, JSON.stringify(this.config));
        this.savedSnapshot = JSON.stringify(this.config);
        this.refreshConfirmState();
        this.flashConfirmed();
        // Le header a rendu son icône au chargement : il faut la redessiner.
        document.dispatchEvent(new CustomEvent("geonerd:avatarchange"));

        if (!AccountSync.isLoggedIn()) {
            this.setStatus("avatar.saved", "ok");
            return;
        }

        // Une validation plus récente ne doit pas se faire écraser par la
        // réponse tardive d'une précédente — ni côté message, ni côté serveur
        // (d'où le chaînage : deux envois rapprochés partent dans l'ordre).
        const requestId = ++this.saveRequestId;
        const config = { ...this.config };
        this.setStatus("avatar.saving", "pending");
        this.savePromise = this.savePromise.then(() => AccountService.patchAvatar(config));
        const { ok } = await this.savePromise;
        if (requestId !== this.saveRequestId) return;
        this.setStatus(ok ? "avatar.savedSynced" : "avatar.saveError", ok ? "ok" : "error");
    }

    setStatus(key, kind) {
        if (!this.status) return;
        // Visible avant d'écrire le texte : un conteneur aria-live en display:none
        // n'annonce rien à son apparition.
        this.status.classList.remove("is-hidden", "is-ok", "is-error", "is-pending");
        this.status.classList.add(`is-${kind}`);
        this.status.textContent = this.t(key);
    }

    clearStatus() {
        if (!this.status) return;
        this.saveRequestId++; // invalide une synchro en vol : son message n'a plus lieu d'être
        this.status.textContent = "";
        this.status.classList.add("is-hidden");
    }

    flashConfirmed() {
        this.confirmBtn.classList.add("is-confirmed");
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => this.confirmBtn.classList.remove("is-confirmed"), CONFIRM_FLASH_MS);
    }

    refreshConfirmState() {
        const dirty = JSON.stringify(this.config) !== this.savedSnapshot;
        this.confirmBtn.disabled = !dirty;
        this.confirmBtn.classList.toggle("is-dirty", dirty);
    }

    renderAll() {
        this.preview.innerHTML = renderAvatarSVG(this.config, 180);
        const swatch = this.stepperRow.querySelector(".avatar-color-swatch");
        if (swatch) swatch.style.backgroundColor = this.config.color;
        this.refreshConfirmState();
    }
}
